<template>
  <div class="page-container">

    <div class="page-header">
      <div class="page-title">
        대리점 목록
        <span class="count-text">총 {{ resolvedTotalCount }} 건</span>
      </div>

      <div class="header-buttons">
        <button class="btn primary" @click="onClickCreate">
          신규 등록
        </button>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="card agency-list-card">

        <!-- 검색 영역 -->
        <div class="filter-section">
          <div class="filter-grid">

            <!-- 검색 유형 -->
            <div class="filter-item">
              <label class="filter-label">검색 조건</label>

              <select
                v-model="filters.searchType"
                class="filter-input"
              >
                <option value="name">대리점명</option>
                <option value="managerName">담당자</option>
                <option value="managerPhone">연락처</option>
                <option value="address">주소</option>
              </select>
            </div>

            <!-- 검색어 -->
            <div class="filter-item filter-item-keyword">
              <label class="filter-label">직접 입력</label>

              <input
                v-model.trim="filters.keyword"
                class="filter-input"
                type="text"
                placeholder="대리점명 / 담당자 / 연락처 / 주소 검색"
                @keyup.enter="onClickSearch"
              />
            </div>

            <!-- 버튼 -->
            <div class="filter-actions">
              <button
                class="btn primary"
                @click="onClickSearch"
                :disabled="loading"
              >
                검색
              </button>

              <button
                class="btn ghost"
                @click="onClickReset"
                :disabled="loading"
              >
                초기화
              </button>
            </div>

          </div>
        </div>

        <!-- 테이블 -->
        <div class="table-section">
          <table v-if="tableRows.length" class="data-table">
            <thead>
              <tr>
                <th>대리점명</th>
                <th>담당자</th>
                <th>연락처</th>
                <th>주소</th>
                <th>현장 수</th>
                <th class="text-center">작업</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in tableRows" :key="row.id">
                <td class="td-wrap td-name" :title="row.name">
                  {{ row.name || '-' }}
                </td>

                <td>{{ row.managerName || '-' }}</td>

                <td>{{ row.managerPhone || '-' }}</td>

                <td class="td-wrap td-address" :title="fullAddress(row)">
                  {{ fullAddress(row) || '-' }}
                </td>

                <td>
                  {{ row.siteCount ?? 0 }}
                </td>

                <td class="td-actions">
                  <div class="row-actions">
                    <button
                      class="agency-action-btn detail"
                      @click="onClickDetail(row)"
                    >
                      상세보기
                    </button>

                    <button
                      class="agency-action-btn delete"
                      @click="toggleStatus(row)"
                    >
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="table-empty">
            표시할 대리점 데이터가 없습니다
          </div>
        </div>

        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="goToPage"
        />

      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

export default {
  name: 'AgencyList',

  components: {
    PaginationBar
  },

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      size: 15,
      groupSize: 5,
      loading: false,

      filters: {
        searchType: 'name',
        keyword: '',
      },
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    }
  },

  mounted() {
    this.fetchAgencyList()
  },

  methods: {
    normalizedStatus(row) {
      if (row?.status === '정상') return '정상'
      if (row?.status === '삭제') return '정지'
      if (row?.status === '정지') return '정지'
      if (row?.deleted === true) return '정지'
      return '정상'
    },

    async toggleStatus(row) {

      const firstOk = confirm(
        `"${row.name}" 대리점을 삭제하시겠습니까?\n\n삭제 후 목록에서 표시되지 않습니다.`
      )

      if (!firstOk) return

      const secondOk = confirm(
        `정말로 삭제하시겠습니까?\n\n삭제 시 복구가 불가합니다.\n계속 진행하려면 확인을 눌러주세요.`
      )

      if (!secondOk) return

      try {
        await api.post('/app/agency/update', {
          id: row.id,
          deleted: true
        })

        alert('삭제 완료')
        await this.fetchAgencyList()

      } catch (e) {
        console.error(e)
        alert('처리 실패')
      }
    },

    async fetchAgencyList() {
      this.loading = true

      try {
        const params = {
          page: this.currentPage,
          size: this.size,
          orderBy: 'createdAtDesc',
          deleted: false
        }

        if (this.filters.keyword) {
          params[this.filters.searchType] = this.filters.keyword
        }

        const res = await api.get('/app/agency/list', { params })
        const page = res.data?.result

        const rows = Array.isArray(page?.content) ? page.content : []

        this.totalCount = Number(page?.totalElements || 0)
        this.totalPages = Number(page?.totalPages || 0)
        this.currentPage = Number(page?.number || 0)

        this.tableRows = await Promise.all(
          rows.map(async row => ({
            ...row,
            siteCount: await this.fetchAgencySiteCount(row.id)
          }))
        )
      } catch (e) {
        console.error(e)
        this.tableRows = []
        this.totalCount = 0
        this.totalPages = 0
      } finally {
        this.loading = false
      }
    },

    async fetchAgencySiteCount(agencyId) {
      if (!agencyId) return 0

      try {
        const res = await api.get('/app/agency/sites', {
          params: { agencyId }
        })

        const list = res.data?.result
        return Array.isArray(list) ? list.length : 0
      } catch (e) {
        console.error('대리점 연동 현장 수 조회 실패', e)
        return 0
      }
    },

    onClickSearch() {
      this.currentPage = 0
      this.fetchAgencyList()
    },

    onClickReset() {
      this.filters.searchType = 'name'
      this.filters.keyword = ''
      this.currentPage = 0
      this.fetchAgencyList()
    },

    fullAddress(row) {
      return [row.address, row.addressDetail].filter(Boolean).join(' ')
    },

    goToPage(page) {
      if (page < 0 || page >= this.totalPages) return
      this.currentPage = page
      this.fetchAgencyList()
    },

    onClickCreate() {
      this.$emit('create')
    },

    onClickDetail(row) {
      this.$emit('detail', row)
    }
  }
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-card.css";
@import "@/styles/admin/admin-pagination.css";
@import "@/styles/admin/admin-input.css";

.agency-list-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* 테이블 */
.table-section {
  flex: 1;
}

.data-table {
  table-layout: auto;
  min-width: 100%;
}

.data-table th:nth-child(1),
.data-table td:nth-child(1) {
  width: 22%;
}

.data-table th:nth-child(2),
.data-table td:nth-child(2) {
  width: 100px;
}

.data-table th:nth-child(3),
.data-table td:nth-child(3) {
  width: 120px;
}

.data-table th:nth-child(4),
.data-table td:nth-child(4) {
  width: auto;
}

.data-table th:nth-child(5),
.data-table td:nth-child(5) {
  width: 80px;
}

.data-table th:nth-child(6),
.data-table td:nth-child(6) {
  width: 130px;
}

.td-wrap {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.td-name {
  min-width: 180px;
}

.td-address {
  min-width: 360px;
  text-align: left;
}

.table-empty {
  min-height: 240px;
}

@media (max-width: 900px) {
  .keyword-search-wrap {
    grid-template-columns: 1fr;
  }

  .search-action-group {
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .data-table {
    min-width: 1100px;
  }
}

/* =========================
   FILTER
========================= */

.filter-section {
  margin-bottom: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
}

.filter-grid {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) auto;
  gap: 14px 12px;
  align-items: end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.filter-input {
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #d7dde5;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.filter-input:focus {
  outline: none;
  border-color: #e58a13;
  box-shadow: 0 0 0 4px rgba(229, 138, 19, 0.12);
}

select.filter-input {
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #6b7280 50%),
    linear-gradient(135deg, #6b7280 50%, transparent 50%);
  background-position:
    calc(100% - 18px) calc(50% - 3px),
    calc(100% - 12px) calc(50% - 3px);
  background-size: 6px 6px;
  background-repeat: no-repeat;
  padding-right: 40px;
}

.filter-item-keyword {
  min-width: 0;
}

.filter-actions {
  display: flex;
  align-items: end;
  justify-content: flex-end;
  gap: 8px;
  white-space: nowrap;
}

.filter-actions .btn {
  height: 40px;
  min-width: 82px;
}

/* =========================
   RESPONSIVE
========================= */

@media (max-width: 1024px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
}

.td-actions {
  text-align: center;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.agency-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 64px;
  height: 28px;
  padding: 0 10px;

  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;

  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease,
    transform 0.12s ease,
    box-shadow 0.18s ease;
}

.agency-action-btn.detail {
  background: #e58a13;
  border: 1px solid #e58a13;
  color: #ffffff;
}

.agency-action-btn.detail:hover {
  background: #c9750f;
  border-color: #c9750f;

  transform: translateY(-2px);

  box-shadow:
    0 10px 22px rgba(229,138,19,.30);
}

.agency-action-btn.delete {
  background: #ef4444;
  border: 1px solid #ef4444;
  color: #ffffff;
}

.agency-action-btn.delete:hover {
  background: #dc2626;
  border-color: #dc2626;

  transform: translateY(-2px);

  box-shadow:
    0 10px 22px rgba(239,68,68,.28);
}
</style>